/**
 * Provides an administrative interface for configuring the Upcoming Meetings
 * widget integration settings. Allows administrators to manage the Outlook
 * Meetings URL used by the widget, ensuring users are directed to the correct
 * meeting and calendar experience. Supports retrieving existing widget
 * configuration values, updating integration endpoints, tracking unsaved
 * changes, and persisting configuration updates for the meetings dashboard.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.upcomingmeetings";

const MeetingsConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [outlookMeetingsUrl, setOutlookMeetingsUrl] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const meetingsConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(meetingsConfig);

    setOutlookMeetingsUrl(
      meetingsConfig.OutlookMeetingsUrl || "",
    );
  };

  const hasChanges =
    !!savedConfig &&
    outlookMeetingsUrl !==
      (savedConfig.OutlookMeetingsUrl || "");

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          OutlookMeetingsUrl: outlookMeetingsUrl,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setOutlookMeetingsUrl(
      savedConfig.OutlookMeetingsUrl || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Outlook Meetings URL
        </span>

        <input
          type="text"
          value={outlookMeetingsUrl}
          onChange={(e) => setOutlookMeetingsUrl(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Outlook Meetings URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default MeetingsConfig;
